import React, { useEffect, useState } from 'react';
import { API_BASE, authHeaders } from '../../utils/api';
import '../../styles/common.css';
import './EnquireNow.css';

const INTERESTS = ['admissions', 'student section', 'account section', 'hostel', 'exam section', 'placement'];

const ContactUs = () => {
  const [contacts, setContacts] = useState([]);
  const [settings, setSettings] = useState({ formEnabled: true });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [interestFilter, setInterestFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('enquiries');

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [contactsRes, settingsRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/contact-us${statusFilter !== 'all' ? `?status=${statusFilter}` : ''}`, { headers: authHeaders() }),
        fetch(`${API_BASE}/api/admin/contact-us/settings`, { headers: authHeaders() })
      ]);
      const contactsData = await contactsRes.json();
      const settingsData = await settingsRes.json();
      if (contactsRes.ok) setContacts(contactsData.data || []);
      if (settingsRes.ok) setSettings(settingsData.data || { formEnabled: true });
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/contact-us/${id}/status`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setSuccess(data.message);
      fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contact submission?')) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/contact-us/${id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setSuccess(data.message);
      fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleFormStatus = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/contact-us/settings/toggle-form`, {
        method: 'POST',
        headers: authHeaders()
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setSuccess('Form status updated');
      fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  // All contacts from contact_us table filtered by status (already applied via API)
  // For "Student Enquiries" tab, filter by selected interest from contact_us table
  const handleExport = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/contact-us/export/csv`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `contact_us_${Date.now()}.csv`;
      document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url); document.body.removeChild(a);
      setSuccess('Exported successfully');
    } catch (err) { setError(err.message); }
  };

  const filteredByInterest = contacts.filter(c =>
    interestFilter === 'all' || c.interest === interestFilter
  );

  const renderTable = (rows, isStudentEnquiries = false) => (
    loading ? (
      <div className="loading-state">Loading...</div>
    ) : rows.length === 0 ? (
      <div className="empty-state">No submissions found.</div>
    ) : (
      <table className="admin-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Phone</th>
            <th>Department</th>
            <th>Message</th>
            <th>Date</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id}>
              <td>{c.name}</td>
              <td>{c.email}</td>
              <td>{c.phone}</td>
              <td style={{ textTransform: 'capitalize' }}>{c.interest}</td>
              <td style={{ maxWidth: '200px', wordBreak: 'break-word' }}>{c.message}</td>
              <td>{new Date(c.created_at).toLocaleDateString()}</td>
              <td><span className={`status status-${c.status}`}>{c.status}</span></td>
              <td className="actions">
                {isStudentEnquiries ? (
                  <>
                    <button className="btn-info" onClick={() => handleStatusUpdate(c.id, 'contacted')}>📞 Contact</button>
                    <button className="btn-success" onClick={() => handleStatusUpdate(c.id, 'enrolled')}>✅ Enroll</button>
                    <button className="btn-danger" onClick={() => handleStatusUpdate(c.id, 'rejected')}>❌ Reject</button>
                  </>
                ) : (
                  <>
                    {c.status !== 'replied' && (
                      <button className="btn-success" onClick={() => handleStatusUpdate(c.id, 'replied')}>✅ Replied</button>
                    )}
                    <button className="btn-danger" onClick={() => handleDelete(c.id)}>🗑️ Delete</button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )
  );

  return (
    <div className="admin-page">
      <div className="page-heading">
        <h1>Contact Us Management</h1>
        <p>Manage contact form submissions from the website.</p>
      </div>

      {error && <div className="admin-alert error" onClick={() => setError('')}>{error}</div>}
      {success && <div className="admin-alert success" onClick={() => setSuccess('')}>{success}</div>}

      <div className="tab-navigation">
        <button className={`tab-btn ${activeTab === 'enquiries' ? 'active' : ''}`} onClick={() => setActiveTab('enquiries')}>
          Student Enquiries
        </button>
        <button className={`tab-btn ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>
          Form Settings
        </button>
      </div>

      {/* Student Enquiries Tab */}
      {activeTab === 'enquiries' && (
        <div className="tab-content">
          <div className="content-section">
            <div className="section-header">
              <h2>Student Enquiries</h2>
              <div className="filter-controls" style={{ display: 'flex', gap: '10px', alignItems:'center' }}>
                <button onClick={handleExport} className="btn-secondary">📥 Export CSV</button>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="contacted">Contacted</option>
                  <option value="enrolled">Enrolled</option>
                  <option value="rejected">Rejected</option>
                </select>
                <select value={interestFilter} onChange={(e) => setInterestFilter(e.target.value)}>
                  <option value="all">All Departments</option>
                  {INTERESTS.map(i => (
                    <option key={i} value={i}>{i.charAt(0).toUpperCase() + i.slice(1)}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="table-container">{renderTable(filteredByInterest, true)}</div>
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="tab-content">
          <div className="content-section">
            <h2>Form Settings</h2>
            <div className="settings-card">
              <div className="setting-item">
                <div className="setting-info">
                  <h3>Contact Form Status</h3>
                  <p>Enable or disable the public contact form on the website</p>
                  <p><strong>Current Status:</strong> <span className={`status status-${settings.formEnabled ? 'approved' : 'rejected'}`}>{settings.formEnabled ? 'Enabled' : 'Disabled'}</span></p>
                </div>
                <div className="setting-control">
                  {settings.formEnabled ? (
                    <button className="btn-danger" onClick={toggleFormStatus}>🚫 Disable Form</button>
                  ) : (
                    <button className="btn-success" onClick={toggleFormStatus}>✅ Enable Form</button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactUs;
